import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { IconButton, Typography } from '@sm-dashboard/ui-components';
import { Visibility, Delete } from '@mui/icons-material';
import { GridData } from '@sm-dashboard/grid-data';
import { ColumnDefinition } from '@sm-dashboard/types';
import instanceAxios from '@/util/axios';
import { businessAdsAdminUrl, deleteBusinessAdUrl } from '@/constants';
import ViewAdDialog from '@/components/dashboard/BusinessAds/admin/business-ads/ViewAdDialog';
import AdminSearchAndFilters from '@/components/dashboard/BusinessAds/AdminSearchAndFilters';
import { useSearchParams } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { useAppContext } from '@/context/AppContext';
import { Select, Dialog } from '@sm-dashboard/ui-components';

export interface IFrontendBusinessAd {
    id: string;
    submitterType: 'shaliach' | 'publisher';
    status: 'pending' | 'approved' | 'rejected';
    businessName: string;
    shaliachName?: string;
    createdAt: string;
    paymentPlan?: 'monthly' | 'half-yearly' | 'yearly' | 'none';
    approvedWithoutPayment?: boolean;
    paymentSessionData?: {
        lowProfileId?: string;
        paymentId?: number;
        transactionId?: string;
        responseCode?: number;
        approvalNumber?: string;
        documentNumber?: string;
        documentUrl?: string;
        paymentDate?: Date;
    };
    [key: string]: unknown;
}

const AdminBusinessAdsPage = () => {
    const [selectedAd, setSelectedAd] = useState<IFrontendBusinessAd | null>(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [statusSubmitting, setStatusSubmitting] = useState(false);
    const [adToDelete, setAdToDelete] = useState<IFrontendBusinessAd | null>(null);
    const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
    const [statusChangeData, setStatusChangeData] = useState<{
        ad: IFrontendBusinessAd;
        newStatus: 'approved' | 'rejected' | 'pending';
    } | null>(null);
    const [searchParams] = useSearchParams();
    const { data, loading, fetchData } = useFetch();
    const { setSnackbar } = useAppContext();

    // Fetch data when search params change
    const searchParamsString = searchParams.toString();
    useEffect(() => {
        const fetchAds = async () => {
            await fetchData({
                url: `${businessAdsAdminUrl}?${searchParamsString}`,
                method: 'GET'
            });
        };
        fetchAds();
    }, [searchParamsString]);

    const handleStatusChangeRequest = (ad: IFrontendBusinessAd, newStatus: 'approved' | 'rejected' | 'pending') => {
        setStatusChangeData({ ad, newStatus });
        setStatusChangeDialogOpen(true);
    };

    const handleConfirmStatusChange = async () => {
        if (!statusChangeData) return;

        const { ad, newStatus } = statusChangeData;
        setStatusSubmitting(true);

        try {
            let response;
            if (newStatus === 'approved') {
                // For approval, use the approve-and-pay endpoint
                response = await instanceAxios.patch(`${businessAdsAdminUrl}/${ad.id}/approve-and-pay`);

                // If we got here, both approval and payment (if publisher) were successful
                if (response.data.isPublisher) {
                    setSnackbar({
                        open: true,
                        message: 'המודעה אושרה והתשלום בוצע בהצלחה! ✅',
                        severity: 'success',
                    });
                } else {
                    // For shaliach ads
                    setSnackbar({
                        open: true,
                        message: 'המודעה אושרה בהצלחה ✅',
                        severity: 'success',
                    });
                }
            } else {
                // For rejection or pending, use the regular status update
                response = await instanceAxios.patch(`${businessAdsAdminUrl}/${ad.id}/status`, { status: newStatus });
                const statusMessage = newStatus === 'rejected' ? 'המודעה נדחתה' : 'המודעה הוחזרה למצב ממתין';
                setSnackbar({
                    open: true,
                    message: statusMessage,
                    severity: 'info',
                });
            }

            // Refresh data after status update
            const fetchAds = async () => {
                await fetchData({
                    url: `${businessAdsAdminUrl}?${searchParamsString}`,
                    method: 'GET'
                });
            };
            fetchAds();
        } catch (error: any) {
            console.error(`Failed to update ad status to ${newStatus}`, error);

            // Show error message
            const errorMessage = error?.response?.data?.message || 'שגיאה בעדכון סטטוס המודעה';
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
            });
        } finally {
            setStatusSubmitting(false);
            setStatusChangeDialogOpen(false);
            setStatusChangeData(null);
        }
    };

    const handleCancelStatusChange = () => {
        setStatusChangeDialogOpen(false);
        setStatusChangeData(null);
    };

    const handleApproveWithoutPayment = async () => {
        if (!statusChangeData) return;

        const { ad } = statusChangeData;
        setStatusSubmitting(true);

        try {
            // Call the new approve-without-payment endpoint
            const response = await instanceAxios.patch(`${businessAdsAdminUrl}/${ad.id}/approve-without-payment`);

            setSnackbar({
                open: true,
                message: 'המודעה אושרה ללא תשלום בהצלחה! ✅',
                severity: 'success',
            });

            // Refresh data after approval
            const fetchAds = async () => {
                await fetchData({
                    url: `${businessAdsAdminUrl}?${searchParamsString}`,
                    method: 'GET'
                });
            };
            fetchAds();
        } catch (error: any) {
            console.error('Failed to approve ad without payment', error);

            // Show error message
            const errorMessage = error?.response?.data?.message || 'שגיאה באישור המודעה ללא תשלום';
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
            });
        } finally {
            setStatusSubmitting(false);
            setStatusChangeDialogOpen(false);
            setStatusChangeData(null);
        }
    };

    const handleViewAd = (ad: IFrontendBusinessAd) => {
        setSelectedAd(ad);
        setViewDialogOpen(true);
    };

    const handleCloseViewDialog = () => {
        setViewDialogOpen(false);
        setSelectedAd(null);
    };

    const handleAdUpdated = async () => {
        // Refresh data after ad update
        await fetchData({
            url: `${businessAdsAdminUrl}?${searchParamsString}`,
            method: 'GET'
        });
        setSnackbar({
            open: true,
            message: 'המודעה עודכנה בהצלחה ✅',
            severity: 'success',
        });
    };


    const handleDeleteClick = (ad: IFrontendBusinessAd) => {
        setAdToDelete(ad);
        setDeleteDialogOpen(true);
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setAdToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!adToDelete) return;

        setDeleting(true);
        try {
            await instanceAxios.delete(`${deleteBusinessAdUrl}/${adToDelete.id}`);

            setSnackbar({
                open: true,
                message: 'המודעה נמחקה בהצלחה',
                severity: 'success',
            });

            // Refresh data after deletion
            const fetchAds = async () => {
                await fetchData({
                    url: `${businessAdsAdminUrl}?${searchParamsString}`,
                    method: 'GET'
                });
            };
            fetchAds();

            setDeleteDialogOpen(false);
            setAdToDelete(null);
        } catch (error: any) {
            console.error('Failed to delete ad', error);

            const errorMessage = error?.response?.data?.message || 'שגיאה במחיקת המודעה';
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
            });
        } finally {
            setDeleting(false);
        }
    };


    const columns: ColumnDefinition<IFrontendBusinessAd>[] = [
        { field: 'businessName', headerName: 'שם העסק' },
        { field: 'submitterType', headerName: 'סוג מפרסם' },
        {
            field: 'paymentPlan',
            headerName: 'מסלול תשלום',
            valueGetter: (row: IFrontendBusinessAd) => {
                // Only show payment plan for publisher submitter type
                if (row.submitterType === 'publisher') {
                    const planMap: Record<string, string> = {
                        'monthly': '3 חודשים',
                        'half-yearly': '6 חודשים',
                        'yearly': '12 חודשים',
                        'none': 'ללא תשלום'
                    };
                    return planMap[row.paymentPlan || 'none'] || 'לא צוין';
                }
                return '-';
            },
        },
        {
            field: 'shaliachName',
            headerName: 'שם השליח',
            valueGetter: (row: IFrontendBusinessAd) => {
                // Only show shaliach name for shaliach submitter type
                if (row.submitterType === 'shaliach') {
                    return row.shaliachName || 'לא צוין';
                }
                return '-';
            },
        },
        {
            field: 'createdAt',
            headerName: 'תאריך יצירה',
            valueGetter: (row: IFrontendBusinessAd) => {
                if (!row.createdAt) return '';
                try {
                    return new Date(row.createdAt).toLocaleString('he-IL');
                } catch (error) {
                    console.error('Error parsing date:', row.createdAt, error);
                    return row.createdAt;
                }
            },
        },
        {
            field: 'view',
            headerName: 'צפייה',
            renderCell: (row: IFrontendBusinessAd) => {
                if (!row) {
                    return null;
                }
                return (
                    <IconButton
                        shape="circle"
                        color="primary"
                        size="medium"
                        aria-label="צפייה במודעה"
                        onClick={() => handleViewAd(row)}
                    >
                        <Visibility />
                    </IconButton>
                );
            },
        },
        {
            field: 'delete',
            headerName: 'מחיקה',
            renderCell: (row: IFrontendBusinessAd) => {
                if (!row) {
                    return null;
                }
                // Only show delete button for non-approved ads
                if (row.status === 'approved') {
                    return null;
                }
                return (
                    <IconButton
                        shape="circle"
                        color="error"
                        size="medium"
                        aria-label="מחק מודעה"
                        onClick={() => handleDeleteClick(row)}
                    >
                        <Delete />
                    </IconButton>
                );
            },
        },
        {
            field: 'actions',
            headerName: 'עדכון סטטוס',
            renderCell: (row: IFrontendBusinessAd) => {
                if (!row) {
                    return null;
                }
                return (
                    <Select
                        variant="chip"
                        value={row.status}
                        onChange={(value: string) => {
                            const newStatus = value as 'approved' | 'rejected' | 'pending';
                            if (newStatus !== row.status) {
                                handleStatusChangeRequest(row, newStatus);
                            }
                        }}
                        options={[
                            { value: 'pending', label: 'ממתין', backgroundColor: 'secondary' },
                            { value: 'approved', label: 'אישור', backgroundColor: 'success' },
                            { value: 'rejected', label: 'דחייה', backgroundColor: 'error' }
                        ]}
                    />
                );
            },
        },
    ];

    return (
      <>
        <AdminSearchAndFilters />
        <Box sx={{ height: 'calc(100vh - 300px)', width: '100%' }}>
          <GridData
            data={data?.rows || []}
            columns={columns}
            loading={loading}
            pagination={{
              count: data?.count || 0
            }}
            style={{
              conditions: [
                {
                  when: (row: IFrontendBusinessAd) => row.status === 'approved',
                  style: {
                    backgroundColor: '#f5f5f5'
                  }
                }
              ]
            }}
          />
        </Box>

        <ViewAdDialog
          open={viewDialogOpen}
          onClose={handleCloseViewDialog}
          ad={selectedAd}
          onAdUpdated={handleAdUpdated}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          title="מחיקת מודעה"
          cancelAction={{ disabled: deleting }}
          primaryAction={{ label: 'מחק', onClick: handleConfirmDelete, loading: deleting, color: 'error' }}
        >
          <Typography variant="body" color="text.secondary">
            האם אתה בטוח שברצונך למחוק את המודעה "{adToDelete?.businessName}"?
            <br />
            פעולה זו אינה ניתנת לביטול.
          </Typography>
        </Dialog>

        {/* Status Change Confirmation Dialog */}
        <Dialog
          open={statusChangeDialogOpen}
          onClose={handleCancelStatusChange}
          title={
            statusChangeData?.newStatus === 'approved' && statusChangeData?.ad.submitterType === 'publisher'
              ? 'אזהרה: אישור מודעה של מפרסם'
              : 'אישור שינוי סטטוס'
          }
          cancelAction={{ disabled: statusSubmitting }}
          secondaryActions={
            statusChangeData?.newStatus === 'approved' && statusChangeData?.ad.submitterType === 'publisher'
              ? [
                  {
                    label: 'אישור ללא תשלום',
                    onClick: handleApproveWithoutPayment,
                    color: 'info',
                    loading: statusSubmitting
                  }
                ]
              : undefined
          }
          primaryAction={
            statusChangeData?.newStatus === 'approved' && statusChangeData?.ad.submitterType === 'publisher'
              ? {
                  label: 'אישור עם תשלום',
                  onClick: handleConfirmStatusChange,
                  color: 'success',
                  loading: statusSubmitting
                }
              : {
                  label: 'אישור',
                  onClick: handleConfirmStatusChange,
                  color: statusChangeData?.newStatus === 'approved' ? 'success' : 'primary',
                  loading: statusSubmitting
                }
          }
        >
          <Typography variant="body" color="text.secondary">
            {statusChangeData?.newStatus === 'approved' && statusChangeData?.ad.submitterType === 'publisher' ? (
              <>
                <strong>
                  <span role="img" aria-label="אזהרה">
                    ⚠️
                  </span>{' '}
                  בחר אופציית אישור:
                </strong>
                <br />
                <br />
                <strong>אישור עם תשלום:</strong> יבצע את התשלום דרך מערכת התשלום ויאשר את המודעה.
                <br />
                <em>שים לב: עלול לגרום לתשלום חוזר אם המודעה כבר שולמה בעבר.</em>
                <br />
                <br />
                <strong>אישור ללא תשלום:</strong> יאשר את המודעה ללא ביצוע תשלום.
                <br />
                המודעה תפורסם ותופיע בלוח לפי התקופה שנבחרה.
                <br />
                <br />
                מודעה: "{statusChangeData?.ad.businessName}"
              </>
            ) : (
              <>
                האם אתה בטוח שברצונך לשנות את סטטוס המודעה "{statusChangeData?.ad.businessName}" ל-
                {statusChangeData?.newStatus === 'approved'
                  ? 'מאושר'
                  : statusChangeData?.newStatus === 'rejected'
                  ? 'דחוי'
                  : 'ממתין'}
                ?
              </>
            )}
          </Typography>
        </Dialog>
      </>
    );
};

export default AdminBusinessAdsPage;